// FIX-TV-PRELOGIN-A I1 (TVB-10) — the forced app update, kept out of the farmer's way.
//
// Today (public/sw.js + hooks/usePWA.ts): a new build's service worker installs in the background and then WAITS —
// it only takes over when the farmer taps "Update" on the banner or when every tab of the app is closed. HTML is
// served network-first, so an online farmer already runs the new JS on the next open; what waits is the new
// worker's precache (the offline copy of every screen), and the two builds' caches sit side by side until then.
//
// The rule here: the page asks the waiting worker to take over as soon as NO form field holds input and nothing is
// being typed, and reloads itself ONCE after the worker took over — never mid-typing. While a field has input the
// swap is deferred and re-checked when a field loses focus, a form is submitted, or the app comes back into view
// (the next open of a backgrounded PWA). The training-video cache is untouched: sw.js keeps every agxl-training-*
// cache on activate (B7a §9.3.1).

const NON_TEXT_INPUT_TYPES = new Set(['checkbox', 'radio', 'hidden', 'button', 'submit', 'reset', 'file', 'image', 'range', 'color']);

function isTextField(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(el.type);
  if (el instanceof HTMLTextAreaElement) return true;
  return isEditable(el);
}

// contenteditable="" / "true" (jsdom has no isContentEditable; the attribute is what the app would set)
function isEditable(el: Element): boolean {
  const a = el.getAttribute('contenteditable');
  return a === '' || a === 'true' || (el as HTMLElement).isContentEditable === true;
}

/** True when any text-like field on the page holds input, or one is focused (the farmer is typing). */
export function formHasInput(doc: Document = document): boolean {
  if (isTextField(doc.activeElement)) return true;
  for (const el of Array.from(doc.querySelectorAll('input, textarea, [contenteditable]'))) {
    if (el instanceof HTMLInputElement) {
      if (NON_TEXT_INPUT_TYPES.has(el.type)) continue;
      if (el.value.trim() !== '') return true;
    } else if (el instanceof HTMLTextAreaElement) {
      if (el.value.trim() !== '') return true;
    } else if (isEditable(el) && (el.textContent ?? '').trim() !== '') {
      return true;
    }
  }
  return false;
}

/** FIX-TV-PRELOGIN-A2: a worker's SW_BUILD stamp is `<commit>-<yyyymmddhhmmss>` (vite.config.ts); the page knows its
 *  own commit (__APP_VERSION__.commit). Same build ⇔ the stamp starts with that commit. An unknown stamp (an older
 *  sw.js that cannot answer) is never "the same". */
export function sameBuild(workerBuild: string | null | undefined, pageCommit: string | null | undefined): boolean {
  const w = (workerBuild ?? '').trim().toLowerCase();
  const c = (pageCommit ?? '').trim().toLowerCase();
  if (!w || !c) return false;
  const stamp = w.split('-')[0];
  return stamp === c || stamp.startsWith(c) || c.startsWith(stamp);
}

/** FIX-TV-PRELOGIN-A2: at most one controllerchange reload per tab per minute (sessionStorage). */
export const RELOAD_GUARD_KEY = 'agxl_pwa_reloaded_at';
export const RELOAD_GUARD_MS = 60_000;

/** The moments a deferred swap is re-checked. */
export const RECHECK_EVENTS: ReadonlyArray<string> = ['focusout', 'submit', 'visibilitychange', 'pageshow'];
/** A safety net while deferred (a field cleared with no focus change). */
export const RECHECK_INTERVAL_MS = 20_000;

/**
 * Run `cb` now when no form field has input; otherwise wait for a safe moment (see RECHECK_EVENTS) and run it once.
 * Returns a cancel function. `visibilitychange` only counts when the page is visible again.
 */
export function whenFormIsClear(cb: () => void, doc: Document = document, win: Window = window): () => void {
  if (!formHasInput(doc)) { cb(); return () => undefined; }
  let done = false;
  const check = () => {
    if (done) return;
    if (doc.visibilityState === 'hidden') return;
    if (formHasInput(doc)) return;
    cancel();
    cb();
  };
  const cancel = () => {
    if (done) return;
    done = true;
    for (const ev of RECHECK_EVENTS) doc.removeEventListener(ev, check, true);
    win.removeEventListener('pageshow', check);
    win.clearInterval(timer);
  };
  for (const ev of RECHECK_EVENTS) doc.addEventListener(ev, check, true);
  win.addEventListener('pageshow', check);
  const timer = win.setInterval(check, RECHECK_INTERVAL_MS);
  return cancel;
}
